import { useCallback, useEffect, useState } from 'react';
import { api, errorText, formatDate, formatQty } from '../api';
import { useAuth } from '../auth';
import { Badge, Card, Empty, Loading, Notice } from '../components/ui';
import type { PickTask, Warehouse } from '../types';

interface Wave {
  id: string;
  number: string;
  warehouse_code: string;
  status: string;
  created_at: string;
  task_count: number;
  open_tasks: number;
  order_count: number;
}

interface ConsolidatedStop {
  location_code: string;
  sku: string;
  article_name: string;
  unit: string;
  lot_code: string | null;
  hazmat: boolean;
  total_qty: number;
  picked_qty: number;
  splits: { task_id: string; container: string; order_number: string; qty: number; picked: number }[];
}

const PRIORITY_LABEL: Record<number, string> = {
  1: 'Express',
  2: 'sehr hoch',
  3: 'hoch',
  5: 'normal',
  7: 'niedrig',
  9: 'nachrangig',
};

/**
 * Der Arbeitsvorrat im Lager: alle offenen Pickaufträge über alle Kundenaufträge
 * hinweg, nach Lagerplatz sortiert. Bewusst eine eigene Seite und nicht in den
 * Auftrag eingebettet - kommissioniert wird nach Weg, nicht nach Auftrag.
 */
export function PickingPage() {
  const { ownerId, can } = useAuth();
  const mayPick = can('admin', 'schichtleiter', 'lagerist', 'disponent');

  const [tasks, setTasks] = useState<PickTask[] | null>(null);
  const [waves, setWaves] = useState<Wave[]>([]);
  const [openWave, setOpenWave] = useState<string | null>(null);
  const [waveDetail, setWaveDetail] = useState<{ wave: Wave; stops: ConsolidatedStop[] } | null>(null);
  const [maxOrders, setMaxOrders] = useState(8);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [warehouseId, setWarehouseId] = useState('');
  const [active, setActive] = useState<string | null>(null);
  const [qty, setQty] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError('');
    try {
      setTasks(await api<PickTask[]>('/api/outbound/tasks', { query: { ownerId, warehouseId } }));
    } catch (err) {
      setError(errorText(err));
    }
  }, [ownerId, warehouseId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    api<Warehouse[]>('/api/warehouse').then(setWarehouses).catch(() => setWarehouses([]));
  }, []);

  const loadWaves = useCallback(async () => {
    try {
      setWaves(await api<Wave[]>('/api/outbound/waves'));
    } catch {
      setWaves([]);
    }
  }, []);

  useEffect(() => {
    void loadWaves();
  }, [loadWaves, message]);

  useEffect(() => {
    if (!openWave) {
      setWaveDetail(null);
      return;
    }
    api<{ wave: Wave; stops: ConsolidatedStop[] }>(`/api/outbound/waves/${openWave}`)
      .then(setWaveDetail)
      .catch((err) => setError(errorText(err)));
  }, [openWave, message]);

  async function buildWave() {
    if (!warehouseId) return setError('Für eine Welle muss ein Lager gewählt sein.');
    setBusy(true);
    setError('');
    try {
      const created = await api<{ wave: Wave }>('/api/outbound/waves', {
        method: 'POST',
        body: { warehouseId, maxOrders },
      });
      setMessage(
        `Welle ${created.wave.number} gebildet: ${created.wave.order_count} Aufträge, ${created.wave.task_count} Positionen.`,
      );
      setOpenWave(created.wave.id);
      await load();
      await loadWaves();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function finishWave(waveId: string) {
    setBusy(true);
    try {
      await api(`/api/outbound/waves/${waveId}/close`, { method: 'POST' });
      setMessage('Welle abgeschlossen. Offene Positionen sind zurück im Arbeitsvorrat.');
      setOpenWave(null);
      await load();
      await loadWaves();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  function start(task: PickTask) {
    setActive(task.id);
    setQty(String(task.qty_planned - task.qty_picked));
    setMessage('');
  }

  async function confirm(task: PickTask) {
    const amount = Number(qty.replace(',', '.'));
    if (!Number.isFinite(amount) || amount <= 0) return setError('Bitte eine gültige Menge angeben.');

    setBusy(true);
    setError('');
    try {
      const result = await api<{ duplicate: boolean; qtyPicked: number; orderStatus: string }>(
        `/api/outbound/orders/${task.order_id}/tasks/${task.id}/pick`,
        {
          method: 'POST',
          body: { movementId: crypto.randomUUID(), qty: amount },
        },
      );
      setMessage(
        result.duplicate
          ? 'Dieser Pick war bereits erfasst - es wurde nichts doppelt gebucht.'
          : `${formatQty(amount, task.unit)} ${task.article_name} von ${task.location_code} in die Versandzone gebracht.` +
            (result.orderStatus === 'kommissioniert'
              ? ` Auftrag ${task.order_number} ist damit vollständig kommissioniert.`
              : ''),
      );
      setActive(null);
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {error && <Notice kind="error">{error}</Notice>}
      {message && <Notice kind="success">{message}</Notice>}

      <div className="toolbar">
        <label className="field">
          <span>Lager</span>
          <select value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)}>
            <option value="">alle Lager</option>
            {warehouses.map((warehouse) => (
              <option key={warehouse.id} value={warehouse.id}>
                {warehouse.code} · {warehouse.name}
              </option>
            ))}
          </select>
        </label>
        <span className="spacer" />
        <div className="small muted">
          {tasks ? `${tasks.length} offene Pickaufträge` : ''}
        </div>
        <label className="field" style={{ maxWidth: 130 }}>
          <span>Fächer am Wagen</span>
          <input
            type="number"
            min={1}
            max={24}
            value={maxOrders}
            onChange={(e) => setMaxOrders(Number(e.target.value) || 8)}
          />
        </label>
        {mayPick && (
          <button className="primary" onClick={buildWave} disabled={busy}>
            Welle bilden
          </button>
        )}
        <button onClick={() => void load()}>Aktualisieren</button>
      </div>

      {waves.length > 0 && (
        <Card title="Offene Wellen" padded={false}>
          <table>
            <thead>
              <tr>
                <th>Welle</th>
                <th>Lager</th>
                <th className="num">Aufträge</th>
                <th className="num">Positionen</th>
                <th className="num">offen</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {waves.map((wave) => (
                <tr key={wave.id}>
                  <td className="mono">{wave.number}</td>
                  <td className="mono small">{wave.warehouse_code}</td>
                  <td className="num">{wave.order_count}</td>
                  <td className="num">{wave.task_count}</td>
                  <td className="num">{wave.open_tasks}</td>
                  <td className="num">
                    <button className="small ghost" onClick={() => setOpenWave(wave.id)}>
                      Pickliste
                    </button>
                    <button className="small ghost" onClick={() => finishWave(wave.id)} disabled={busy}>
                      abschliessen
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {waveDetail && (
        <Card
          title={`Pickliste ${waveDetail.wave.number}`}
          actions={
            <button className="small" onClick={() => setOpenWave(null)}>
              schliessen
            </button>
          }
          padded={false}
        >
          <div className="body small muted" style={{ paddingBottom: 0 }}>
            Ein Halt je Lagerplatz. Gesamtmenge greifen, dann auf die Fächer verteilen.
          </div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Platz</th>
                  <th>Artikel</th>
                  <th>Charge</th>
                  <th className="num">Gesamt greifen</th>
                  <th>Verteilen auf</th>
                </tr>
              </thead>
              <tbody>
                {waveDetail.stops.map((stop, index) => (
                  <tr key={index}>
                    <td className="mono" style={{ fontWeight: 600, fontSize: 15 }}>
                      {stop.location_code}
                    </td>
                    <td>
                      <span className="mono">{stop.sku}</span> {stop.article_name}
                      {stop.hazmat && (
                        <>
                          {' '}
                          <Badge kind="bad">Gefahrgut</Badge>
                        </>
                      )}
                    </td>
                    <td className="small">{stop.lot_code ?? '—'}</td>
                    <td className="num" style={{ fontSize: 16, fontWeight: 600 }}>
                      {formatQty(stop.total_qty - stop.picked_qty, stop.unit)}
                    </td>
                    <td className="small">
                      {stop.splits.map((split) => (
                        <div key={split.task_id}>
                          <strong className="mono">{split.container}</strong> {formatQty(split.qty)} ·{' '}
                          <span className="muted">{split.order_number}</span>
                          {split.picked > 0 && <span className="muted"> (erledigt)</span>}
                        </div>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Card padded={false}>
        {!tasks ? (
          <Loading />
        ) : tasks.length === 0 ? (
          <Empty>
            Nichts zu kommissionieren. Picklisten entstehen im Auftrag über „Pickliste erzeugen".
          </Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Platz</th>
                  <th>Artikel</th>
                  <th>Charge / MHD</th>
                  <th>Auftrag</th>
                  <th>Dringlichkeit</th>
                  <th>Fach</th>
                  <th>Eigentümer</th>
                  <th className="num">Zu picken</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {tasks.map((task) => (
                  <tr key={task.id}>
                    <td className="mono" style={{ fontWeight: 600 }}>
                      {task.location_code}
                    </td>
                    <td>
                      <span className="mono">{task.sku}</span> {task.article_name}
                    </td>
                    <td className="small">
                      {task.lot_code ?? '—'}
                      {task.best_before ? ` · ${formatDate(task.best_before)}` : ''}
                    </td>
                    <td className="mono small">{task.order_number}</td>
                    <td>
                      {task.priority <= 3 ? (
                        <Badge kind="bad">{PRIORITY_LABEL[task.priority] ?? task.priority}</Badge>
                      ) : (
                        <span className="small muted">
                          {PRIORITY_LABEL[task.priority] ?? task.priority}
                        </span>
                      )}
                    </td>
                    <td className="mono small">{task.container ?? '—'}</td>
                    <td className="small">{task.owner_name}</td>
                    <td className="num">
                      {formatQty(task.qty_planned - task.qty_picked, task.unit)}
                      {task.qty_picked > 0 && (
                        <div className="small muted">bereits {formatQty(task.qty_picked)}</div>
                      )}
                    </td>
                    <td className="num" style={{ width: 260 }}>
                      {active === task.id ? (
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                          <input
                            autoFocus
                            style={{ textAlign: 'right', width: 90 }}
                            value={qty}
                            onChange={(e) => setQty(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') void confirm(task);
                              if (e.key === 'Escape') setActive(null);
                            }}
                          />
                          <button className="primary small" onClick={() => confirm(task)} disabled={busy}>
                            buchen
                          </button>
                          <button className="small" onClick={() => setActive(null)}>
                            ×
                          </button>
                        </div>
                      ) : (
                        mayPick && (
                          <button className="small ghost" onClick={() => start(task)}>
                            picken
                          </button>
                        )
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}
